import { cj } from 'src/utils/classNames';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { trimDisplayZeros } from 'src/utils/formatNumber/trimDisplayZeros';
import Env from 'src/environmentVariables/EnvironmentVariables';
import { getMarketPriceDecimals } from 'src/constants/marketDecimalConfig';
import { ORDERBOOK_DISPLAY_TYPE } from 'src/components/Orderbook/useOrderbookDisplayPreference';

interface OrderbookPriceProps {
	price: number;
	side: 'buy' | 'sell';
	zeroPadding: number;
	className: string;
	isCurrentUserLiquidity: boolean | undefined;
	displayType: ORDERBOOK_DISPLAY_TYPE;
}

export const OrderbookRowPrice = ({
	price,
	side,
	zeroPadding,
	className,
	isCurrentUserLiquidity,
	displayType,
}: OrderbookPriceProps) => {
	const set = useVelocityStore((s) => s.set);
	const currentMarket = useVelocityStore((s) => s.selectedMarket.current);

	// Use market-specific decimal precision instead of zeroPadding
	const marketSymbol = currentMarket?.uiSymbols.baseAssetSymbol ?? '';
	const marketDecimals = getMarketPriceDecimals(marketSymbol);
	const decimalPlaces = Math.max(zeroPadding, marketDecimals);

	const formattedPrice = price.toLocaleString(Env.locale, {
		minimumFractionDigits: decimalPlaces,
		maximumFractionDigits: decimalPlaces,
	});

	const handlePriceClick = (selectedPrice: number) => {
		set((state) => {
			if (
				state.tradeForm.orderType === 'market' ||
				state.tradeForm.orderType === 'oracleLimit'
			) {
				state.tradeForm.orderType = 'limit';
			}
			// @ts-expect-error - we want to override the readonly property
			state.tradeForm.priceBoxStringValue = trimDisplayZeros(
				selectedPrice.toString()
			);
		});
	};

	const dotAfterPrice =
		displayType === ORDERBOOK_DISPLAY_TYPE.HORIZONTAL && side === 'buy';

	return (
		<span
			onClick={() => handlePriceClick(price)}
			className={cj(
				'relative inline-flex items-center hover:cursor-pointer hover:brightness-125',
				side === 'buy'
					? 'text-text-positive-green-button'
					: 'text-text-negative-red-button',
				className
			)}
		>
			{formattedPrice}
			{isCurrentUserLiquidity && (
				<div
					className={cj(
						'absolute size-1 rounded-full bg-purple-link',
						dotAfterPrice ? '-right-1.5' : '-left-1.5'
					)}
				/>
			)}
		</span>
	);
};
